import React, { useState, useEffect } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import IntelligencePanel from "@/components/IntelligencePanel";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { DownloadAnswerButton } from "@/components/DownloadAnswerButton";
import { downloadMainsAnswerPdf } from "@/lib/answerPdf";
import PaywallNotice from "@/components/PaywallNotice";
import HandwrittenUpload from "@/components/HandwrittenUpload";
import GSPYQBank from "@/components/mains/GSPYQBank";
import {
  BookOpen, Target, Award, TrendingUp, ArrowRight, Sparkles,
  FileText, MapPin, BarChart3, CircleCheck, AlertCircle, Lightbulb,
  Leaf, Shield, Cpu, Factory, Zap, Globe, Lock, FileCheck, CheckCircle2, Camera, ArrowLeft,
  Search, Pencil
} from "lucide-react";

// GS-III Diagram Components

// 1. Policy Flow Diagram - Shows policy progression
const PolicyFlowDiagram = ({ answer }) => {
  const mainTopic = answer.diagram?.type || "Policy Framework";
  const stages = answer.body?.slice(0, 5).map(b => b.heading) || ["Problem", "Policy", "Implementation", "Outcome", "Review"];
  
  return (
    <div className="border-2 border-emerald-200 rounded-lg p-6 bg-gradient-to-br from-emerald-50 to-teal-50">
      <h4 className="text-sm font-bold text-emerald-800 mb-4 text-center">📊 Policy Flow Diagram</h4>
      <div className="flex flex-col items-center space-y-3">
        <div className="bg-emerald-600 text-white rounded-lg px-6 py-3 shadow-md">
          <p className="text-sm font-bold">{mainTopic}</p>
        </div>
        <div className="flex items-center justify-center w-full">
          {stages.map((stage, idx) => (
            <React.Fragment key={idx}>
              <div className={`flex-1 max-w-32 border-2 rounded-lg px-3 py-2 text-center ${
                idx === 0 ? 'bg-red-100 border-red-300' :
                idx === 1 ? 'bg-amber-100 border-amber-300' :
                idx === 2 ? 'bg-blue-100 border-blue-300' :
                idx === 3 ? 'bg-purple-100 border-purple-300' :
                'bg-emerald-100 border-emerald-300'
              }`}>
                <p className="text-[10px] font-semibold">{stage}</p>
              </div>
              {idx < stages.length - 1 && (
                <div className="text-emerald-500 mx-1">→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 2. Cause-Effect Diagram - Shows cause and effect relationships
const CauseEffectDiagram = ({ answer }) => {
  const centralIssue = answer.diagram?.type || "Central Issue";
  const causes = answer.body?.slice(0, 3).map(b => b.heading) || ["Cause 1", "Cause 2", "Cause 3"];
  const effects = answer.body?.slice(3, 6).map(b => b.heading) || ["Effect 1", "Effect 2", "Effect 3"];
  
  return (
    <div className="border-2 border-orange-200 rounded-lg p-6 bg-gradient-to-br from-orange-50 to-amber-50">
      <h4 className="text-sm font-bold text-orange-800 mb-4 text-center">🎯 Cause-Effect Diagram</h4>
      <div className="flex items-center justify-between gap-4">
        {/* Causes */}
        <div className="flex-1 space-y-2">
          <p className="text-xs font-bold text-red-700 text-center mb-2">CAUSES</p>
          {causes.map((cause, idx) => (
            <div key={idx} className="bg-red-100 border-2 border-red-300 rounded-lg px-3 py-2">
              <p className="text-[10px] font-semibold text-red-900 text-center">{cause}</p>
            </div>
          ))}
        </div>
        
        {/* Central Issue */}
        <div className="flex flex-col items-center">
          <div className="text-orange-500 text-xl">→</div>
          <div className="bg-orange-500 text-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg my-2">
            <p className="text-[10px] font-bold text-center px-2">{centralIssue}</p>
          </div>
          <div className="text-orange-500 text-xl">→</div>
        </div>
        
        {/* Effects */}
        <div className="flex-1 space-y-2">
          <p className="text-xs font-bold text-blue-700 text-center mb-2">EFFECTS</p>
          {effects.map((effect, idx) => (
            <div key={idx} className="bg-blue-100 border-2 border-blue-300 rounded-lg px-3 py-2">
              <p className="text-[10px] font-semibold text-blue-900 text-center">{effect}</p>
            </div>
          ))}
        </div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 3. Comparison Table Diagram
const ComparisonDiagram = ({ answer }) => {
  const rows = answer.body?.slice(0, 5) || [];
  
  return (
    <div className="border-2 border-blue-200 rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50">
      <h4 className="text-sm font-bold text-blue-800 mb-4 text-center">📋 Comparison Table</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="p-2 text-left font-bold border border-blue-700">Aspect</th>
              <th className="p-2 text-left font-bold border border-blue-700">Analysis</th>
              <th className="p-2 text-left font-bold border border-blue-700">Impact</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'}>
                <td className="p-2 border border-blue-200 font-semibold text-blue-900">
                  {row.heading}
                </td>
                <td className="p-2 border border-blue-200 text-slate-700">
                  {row.points?.[0]?.explanation || 'Key point'}
                </td>
                <td className="p-2 border border-blue-200 text-slate-700">
                  {row.points?.[0]?.example || 'Impact'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 4. Value Chain Diagram - Shows progression of value
const ValueChainDiagram = ({ answer }) => {
  const stages = answer.body?.slice(0, 5).map(b => b.heading) || ["Input", "Process", "Output", "Distribution", "Consumer"];
  
  return (
    <div className="border-2 border-purple-200 rounded-lg p-6 bg-gradient-to-br from-purple-50 to-fuchsia-50">
      <h4 className="text-sm font-bold text-purple-800 mb-4 text-center">🔺 Value Chain Diagram</h4>
      <div className="flex items-center justify-center">
        {stages.map((stage, idx) => (
          <React.Fragment key={idx}>
            <div className={`relative ${idx === 0 ? 'w-16' : idx === stages.length - 1 ? 'w-28' : `w-${16 + idx * 3}`}`}>
              <div 
                className={`py-3 px-2 text-center clip-arrow ${
                  idx === 0 ? 'bg-purple-300' :
                  idx === 1 ? 'bg-purple-400' :
                  idx === 2 ? 'bg-purple-500' :
                  idx === 3 ? 'bg-purple-600' :
                  'bg-purple-700'
                }`}
                style={{
                  clipPath: idx === stages.length - 1 ? 'none' : 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%)',
                  borderRadius: idx === stages.length - 1 ? '0.5rem' : '0',
                  marginLeft: idx > 0 ? '-8px' : '0'
                }}
              >
                <p className={`text-[9px] font-bold ${idx < 2 ? 'text-purple-900' : 'text-white'}`}>{stage}</p>
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="flex justify-center mt-4">
        <div className="text-xs text-purple-700 font-medium">Value Addition →</div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 5. Risk Cycle Diagram - Shows cyclical risks
const RiskCycleDiagram = ({ answer }) => {
  const risks = answer.body?.slice(0, 5).map(b => b.heading) || ["Risk 1", "Risk 2", "Risk 3", "Risk 4", "Risk 5"];
  const colors = [
    'bg-red-100 border-red-400 text-red-900',
    'bg-orange-100 border-orange-400 text-orange-900',
    'bg-amber-100 border-amber-400 text-amber-900',
    'bg-yellow-100 border-yellow-400 text-yellow-900',
    'bg-lime-100 border-lime-400 text-lime-900'
  ];
  
  return (
    <div className="border-2 border-red-200 rounded-lg p-6 bg-gradient-to-br from-red-50 to-orange-50">
      <h4 className="text-sm font-bold text-red-800 mb-4 text-center">⭕ Risk Cycle Diagram</h4>
      <div className="relative flex items-center justify-center" style={{ minHeight: '280px' }}>
        {/* Center */}
        <div className="absolute z-10 bg-gradient-to-br from-red-500 to-orange-600 text-white rounded-full w-20 h-20 flex items-center justify-center shadow-lg">
          <p className="text-[10px] font-bold text-center">Risk<br/>Cycle</p>
        </div>
        
        {/* Risk nodes in circle */}
        {risks.map((risk, idx) => {
          const angle = (idx * 360) / risks.length - 90;
          const rad = (angle * Math.PI) / 180;
          const x = Math.cos(rad) * 100;
          const y = Math.sin(rad) * 100;
          
          return (
            <div
              key={idx}
              className={`absolute border-2 rounded-lg px-3 py-2 shadow-sm ${colors[idx]}`}
              style={{
                left: `calc(50% + ${x}px)`,
                top: `calc(50% + ${y}px)`,
                transform: 'translate(-50%, -50%)',
                width: '90px'
              }}
            >
              <p className="text-[9px] font-semibold text-center">{risk}</p>
            </div>
          );
        })}
        
        {/* Circular arrows */}
        <svg className="absolute w-64 h-64 opacity-30" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="35" fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="5,3"/>
        </svg>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// GS-III Syllabus Categories - 6 main subjects
const GS3_SUBJECTS = [
  { label: "Indian Economy", value: "economy" },
  { label: "Agriculture", value: "agriculture" },
  { label: "Science & Technology", value: "science_technology" },
  { label: "Environment & Ecology", value: "environment" },
  { label: "Disaster Management", value: "disaster_management" },
  { label: "Internal Security", value: "security" }
];

const GS3SuitePage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState("home"); // home, answer-writing, evaluation, expected-questions, pyq-pattern
  const [loading, setLoading] = useState(false);
  
  // Answer Writing state
  const [question, setQuestion] = useState("");
  const [marks, setMarks] = useState(10);
  const [wordLimit, setWordLimit] = useState(150);
  const [selectedDiagram, setSelectedDiagram] = useState("PolicyFlow");
  const [answer, setAnswer] = useState(null);
  
  // Evaluation state
  const [evalQuestion, setEvalQuestion] = useState("");
  const [evalAnswer, setEvalAnswer] = useState("");
  const [evaluation, setEvaluation] = useState(null);
  const [evalInputMode, setEvalInputMode] = useState("type"); // "type" or "handwritten"
  
  // Trend-Based Questions state
  const [subject, setSubject] = useState("economy");
  const [expectedData, setExpectedData] = useState(null);
  const [practicedIds, setPracticedIds] = useState([]);
  
  // PYQ Pattern state
  const [pyqSubject, setPyqSubject] = useState("economy");
  const [pyqData, setPyqData] = useState(null);
  const [pyqYearRange, setPyqYearRange] = useState("5");
  
  // Answer usage tracking for FREE users
  const [answerUsage, setAnswerUsage] = useState({ is_pro: false, suite: "GS3", free_full_answer_used: false, answer_type: "full", message: "" });

  const isPro = user?.role === "PRO";
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (!isPro) {
      toast({ description: "Saving answers as PDF is a PRO feature. Upgrade to download." });
      return;
    }
    setDownloadingPdf(true);
    try {
      await downloadMainsAnswerPdf({
        suite: "GS3", question, marks, wordLimit, answer,
        diagramElementId: "gs3-answer-diagram",
        diagramLabel: diagramTypes.find((d) => d.id === selectedDiagram)?.label,
      });
      toast({ description: "PDF downloaded." });
    } catch (e) {
      toast({ variant: "destructive", description: "Could not generate PDF." });
    } finally {
      setDownloadingPdf(false);
    }
  };
  const isFree = user?.role === "FREE";

  // Fetch answer usage on mount
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/answers/usage?suite=GS3`);
        setAnswerUsage(data);
      } catch (err) {
        console.error("Failed to fetch answer usage:", err);
      }
    };
    fetchUsage();
  }, []);

  const diagramTypes = [
    { id: "PolicyFlow", label: "Policy Flow", icon: "📊", description: "Shows policy progression stages" },
    { id: "CauseEffect", label: "Cause-Effect", icon: "🎯", description: "Shows cause and effect relationships" },
    { id: "Comparison", label: "Comparison", icon: "📋", description: "Tabular comparison of aspects" },
    { id: "ValueChain", label: "Value Chain", icon: "🔺", description: "Shows value addition stages" },
    { id: "RiskCycle", label: "Risk Cycle", icon: "⭕", description: "Shows cyclical risk factors" },
  ];

  // Render diagram based on selected type
  const renderDiagram = () => {
    if (!answer) return null;
    
    switch (selectedDiagram) {
      case "CauseEffect":
        return <CauseEffectDiagram answer={answer} />;
      case "Comparison":
        return <ComparisonDiagram answer={answer} />;
      case "ValueChain":
        return <ValueChainDiagram answer={answer} />;
      case "RiskCycle":
        return <RiskCycleDiagram answer={answer} />;
      case "PolicyFlow":
      default:
        return <PolicyFlowDiagram answer={answer} />;
    }
  };

  // Answer Writing Functions
  const handleGenerateAnswer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/answers/mains`, {
        question,
        paper: "GS3",
        marks,
        word_limit: wordLimit,
      });
      
      // Show the answer FIRST
      setAnswer(data);
      toast({ description: "GS-III Answer generated successfully!" });
      
      // For FREE users: Wait 10 seconds AFTER showing answer, then update usage
      if (!isPro && data.usage) {
        setTimeout(() => {
          setAnswerUsage({
            used: data.usage.used,
            limit: data.usage.limit,
            remaining: data.usage.remaining,
            is_pro: false
          });
          toast({ 
            description: data.paywall_message || "Answer generated",
            duration: 5000
          });
        }, 10000);
      } else if (data.usage || data.quota) {
        const q = data.quota;
        setAnswerUsage({
          is_pro: true,
          suite: "GS3",
          free_full_answer_used: false,
          answer_type: "full",
          unlimited: !q,
          limit: q?.limit ?? 10,
          used: q?.used,
          remaining: q?.remaining,
          message: data.paywall_message || (q ? `${q.remaining}/${q.limit} answers left this cycle` : "Unlimited"),
        });
        if (data.paywall_message) toast({ description: data.paywall_message, duration: 6000 });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        toast({ variant: "destructive", description: msg });
      } else {
        toast({ variant: "destructive", description: msg || "Could not generate answer." });
      }
    } finally {
      setLoading(false);
    }
  };

  // Evaluation Functions
  const handleEvaluate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/eval/mains`, {
        question: evalQuestion,
        answer: evalAnswer,
        marks: marks,
        paper: "GS3",
      });
      setEvaluation(data);
      toast({ description: "GS-III Evaluation generated." });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        toast({ variant: "destructive", description: msg });
      } else {
        toast({ variant: "destructive", description: msg || "Could not evaluate answer." });
      }
    } finally {
      setLoading(false);
    }
  };

  // Trend-Based Questions Functions
  const fetchExpectedQuestions = async () => {
    setLoading(true);
    try {
      const res = await managedAxios.post(`${API_BASE}/expected/generate`, {
        paper: "GS3",
        subject: subject,
        num_questions: 15
      });
      setExpectedData(res.data);
      setPracticedIds([]);
      toast({ description: "GS-III Trend-Based Questions generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not load trend-based questions." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "expected-questions") {
      fetchExpectedQuestions();
    }
    if (mode === "pyq-pattern") {
      fetchPyqPattern();
    }
  }, [subject, mode, pyqSubject, pyqYearRange]);

  // PYQ Pattern Functions
  const fetchPyqPattern = async () => {
    setLoading(true);
    try {
      const res = await managedAxios.post(`${API_BASE}/pyq/analyze`, {
        paper: "GS3",
        subject: pyqSubject,
        year_range: parseInt(pyqYearRange)
      });
      setPyqData(res.data);
      toast({ description: "GS-III PYQ Pattern analysis generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not load PYQ pattern." });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPracticed = async (questionId) => {
    try {
      await managedAxios.post(`${API_BASE}/practice/expected`, {
        question_id: questionId,
        state: "GS3",
        bucket: subject,
      });
      setPracticedIds((prev) => (prev.includes(questionId) ? prev : [...prev, questionId]));
    } catch (e) {
      // soft fail
    }
  };

  const handleSendToMains = (questionText, questionId) => {
    if (questionId) {
      handleMarkPracticed(questionId);
    }
    setQuestion(questionText);
    setMode("answer-writing");
  };

  // CHAPTERWISE PYQ BANK VIEW
  if (mode === "pyq-bank") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30">
        <GSPYQBank
          paper="GS3"
          onBack={() => setMode("home")}
          onPractice={(q, m) => {
            setQuestion(q || "");
            if (m) setMarks(m);
            setMode("answer-writing");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>
    );
  }


  // HOME VIEW
  if (mode === "home") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30">
        
        {/* ═══════════════════════════════════════════════════════════
            VIBRANT HEADER - Emerald/Teal Theme for GS-III (Economy/Environment)
            ═══════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            {/* Back button & Title */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => navigate("/mains")}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Factory className="w-7 h-7" />
                    GS-III Suite
                  </h1>
                  <p className="text-emerald-100 text-sm">Economy • Environment • Security</p>
                </div>
              </div>
              
              {/* PRO Badge */}
              <Badge className={`px-3 py-1.5 ${isPro 
                ? 'bg-white/20 text-white border-0' 
                : 'bg-white/10 text-emerald-100 border-0'}`}>
                {isPro ? '⭐ PRO' : '🆓 FREE'}
              </Badge>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">{isPro ? (answerUsage.unlimited ? '∞' : (answerUsage.remaining ?? answerUsage.limit ?? 10)) : answerUsage.free_full_answer_used ? '0' : '1'}</p>
                <p className="text-[10px] text-emerald-200">{isPro && !answerUsage.unlimited ? 'Answers Left' : 'Full Answers'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">∞</p>
                <p className="text-[10px] text-emerald-200">Evaluations</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">20+</p>
                <p className="text-[10px] text-emerald-200">Years PYQ</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          
          {/* ═══════════════════════════════════════════════════════════
              TOPPER TIP BANNER
              ═══════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-emerald-100 to-teal-100 overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-900">GS-III Strategy</p>
                  <p className="text-sm text-emerald-700">Scheme → Gap → Reform approach with max 2-3 data points</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ═══════════════════════════════════════════════════════════
              MAIN FEATURES GRID
              ═══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-2 gap-4">
            {/* Answer Writing */}
            <Card 
              className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("answer-writing")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <BookOpen className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Answer Writing</h3>
                      {!isPro && (
                        <Badge className={`text-xs ${!answerUsage.free_full_answer_used ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {!answerUsage.free_full_answer_used ? '1 FREE' : 'Partial'}
                        </Badge>
                      )}
                      {isPro && !answerUsage.unlimited && typeof answerUsage.remaining === 'number' && (
                        <Badge className={`text-xs ${answerUsage.remaining > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {answerUsage.remaining}/{answerUsage.limit || 10} left
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Generate GS-III model answers with diagrams</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-emerald-600">
                    Start Writing
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Smart Evaluation */}
            <Card 
              className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("evaluation")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Smart Evaluation</h3>
                    <p className="text-sm text-slate-600 mt-1">Scheme-gap-reform analysis feedback</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-cyan-600">
                    Evaluate
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trend-Based Questions */}
            <Card 
              className="border-2 border-amber-200 bg-gradient-to-br from-amber-500/10 to-orange-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("expected-questions")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Trend-Based Questions</h3>
                    <p className="text-sm text-slate-600 mt-1">Predicted from 20+ years of UPSC PYQ patterns</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-amber-600">
                    Explore
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PYQ Pattern */}
            <Card 
              className="border-2 border-violet-200 bg-gradient-to-br from-violet-500/10 to-purple-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("pyq-pattern")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">PYQ Pattern</h3>
                    <p className="text-sm text-slate-600 mt-1">20+ years trend analysis & forecasting</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-violet-600">
                    Analyze
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chapterwise PYQ Bank */}
          <Card
            className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.01] transition-all group overflow-hidden"
            onClick={() => setMode("pyq-bank")}
            data-testid="gs-pyq-open-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Chapterwise PYQ Bank</h3>
                    <Badge className="text-xs bg-green-100 text-green-700">FREE</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">Every GS-III mains question (2013-2026) mapped chapter & subtopic-wise with answer approach</p>
                </div>
                <ArrowRight className="w-5 h-5 text-emerald-500" />
              </div>
            </CardContent>
          </Card>


          {/* ═══════════════════════════════════════════════════════════
              QUICK FEATURES ROW
              ═══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-4 gap-3">
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-3 text-center">
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <Factory className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-xs text-slate-800">Policy Logic</p>
                <p className="text-[9px] text-slate-500">Scheme→Gap→Reform</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-3 text-center">
                <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <BarChart3 className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-xs text-slate-800">Data</p>
                <p className="text-[9px] text-slate-500">2-3 data points</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-3 text-center">
                <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <Leaf className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-xs text-slate-800">Sustainability</p>
                <p className="text-[9px] text-slate-500">Tech + Env</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-3 text-center">
                <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-xs text-slate-800">Outcome</p>
                <p className="text-[9px] text-slate-500">Measurable</p>
              </CardContent>
            </Card>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              SYLLABUS CATEGORIES QUICK ACCESS
              ═══════════════════════════════════════════════════════════ */}
          <Card className="border border-slate-200 bg-white">
            <CardContent className="p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Syllabus Categories</h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {GS3_SUBJECTS.map((sub) => (
                  <Button
                    key={sub.value}
                    variant="outline"
                    size="sm"
                    className="text-xs hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                    onClick={() => {
                      setSubject(sub.value);
                      setMode("expected-questions");
                    }}
                  >
                    {sub.label.split(' ')[0]}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* PRO Upgrade CTA for FREE users */}
          {!isPro && (
            <Card 
              className="border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate("/upgrade")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold font-heading text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                    <p className="text-sm text-slate-600">Unlock unlimited GS-III answers with diagrams & full evaluation</p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer Quote */}
          <div className="text-center pt-4 pb-8">
            <p className="text-sm text-slate-400 italic">&ldquo;Economic progress without sustainable development is empty.&rdquo; — Amartya Sen</p>
          </div>
        </div>
      </div>
    );
  }

  // ANSWER WRITING VIEW
  if (mode === "answer-writing") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-III Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-2">💹 GS-III Answer Writing</h1>
          <p className="text-sm text-emerald-50">
            Economy • Environment • Security • Technology — Policy-driven, outcome-oriented answers
          </p>
        </div>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <form className="space-y-6" onSubmit={handleGenerateAnswer}>
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">QUESTION</label>
                <Textarea
                  rows={4}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Enter your GS-III question (Economy, Environment, Security, Tech)..."
                  required
                  className="text-sm resize-none border-2 focus:border-emerald-400"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">MARKS</label>
                  <div className="flex gap-2">
                    {[10, 15, 20, 25].map((m) => (
                      <Button
                        key={m}
                        type="button"
                        variant={marks === m ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setMarks(m);
                          setWordLimit(m * 15);
                        }}
                        className={marks === m ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                      >
                        {m}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">WORD LIMIT</label>
                  <input
                    type="number"
                    value={wordLimit}
                    onChange={(e) => setWordLimit(parseInt(e.target.value))}
                    className="w-full px-3 py-2 text-sm border-2 border-slate-200 rounded-md focus:border-emerald-400 focus:outline-none"
                    min="50"
                    max="500"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">SELECT DIAGRAM TYPE FOR YOUR ANSWER</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {diagramTypes.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setSelectedDiagram(d.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        selectedDiagram === d.id 
                          ? "bg-emerald-500 border-emerald-600 text-white shadow-md" 
                          : "bg-white border-slate-200 hover:border-emerald-300 hover:bg-emerald-50"
                      }`}
                    >
                      <div className="text-2xl mb-1">{d.icon}</div>
                      <div className={`text-xs font-bold ${selectedDiagram === d.id ? 'text-white' : 'text-slate-800'}`}>
                        {d.label}
                      </div>
                      <div className={`text-[9px] mt-1 ${selectedDiagram === d.id ? 'text-emerald-100' : 'text-slate-500'}`}>
                        {d.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-3"
              >
                {loading ? "Generating GS-III Answer..." : "🚀 Generate GS-III Answer"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Display Answer */}
        {answer && (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">Model Answer</h2>
              <DownloadAnswerButton locked={!isPro} busy={downloadingPdf} onClick={handleDownloadPdf} testid="gs3-download-pdf" />
            </div>
            {/* Question Interpretation */}
            {answer.interpretation && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-emerald-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">Q</div>
                    <h3 className="text-lg font-bold text-emerald-900 uppercase">Syllabus Mapping & Interpretation</h3>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-800">{answer.interpretation}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contextual Introduction */}
            {answer.intro && (
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-emerald-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
                    <h3 className="text-lg font-bold text-emerald-900 uppercase">Contextual Introduction</h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-emerald-200">
                    <p className="text-sm text-slate-800 leading-relaxed">{answer.intro}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Early Diagram / Framework */}
            {answer.diagram && (
              <Card className="border-teal-200 bg-teal-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                    <h3 className="text-lg font-bold text-teal-900 uppercase">Diagram: {diagramTypes.find(d => d.id === selectedDiagram)?.label}</h3>
                  </div>
                  <div id="gs3-answer-diagram" className="bg-white rounded-lg">{renderDiagram()}</div>
                </CardContent>
              </Card>
            )}

            {/* Multi-Dimensional Body */}
            {answer.body && answer.body.length > 0 && (
              <Card className="border-cyan-200 bg-cyan-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
                    <h3 className="text-lg font-bold text-cyan-900 uppercase">Multi-Dimensional Analysis</h3>
                  </div>

                  <div className="space-y-5">
                    {answer.body.map((section, idx) => (
                      <div key={idx} className="space-y-3">
                        <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded-lg p-4 border border-emerald-200">
                          <h4 className="text-sm font-bold text-emerald-900 mb-2">
                            {idx + 1}. {section.heading}
                          </h4>
                        </div>

                        {section.points && section.points.map((point, pidx) => (
                          <div key={pidx} className="bg-white rounded-xl border-2 border-slate-200 p-5 space-y-4">
                            <div>
                              <h5 className="text-sm font-bold text-slate-900 uppercase mb-2">{point.keyword}</h5>
                              <p className="text-sm text-slate-700 leading-relaxed">{point.explanation}</p>
                            </div>

                            {point.example && (
                              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-3 flex items-start gap-2">
                                <Factory className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-semibold text-blue-900 mb-1">Policy Example</p>
                                  <p className="text-xs text-blue-800">{point.example}</p>
                                </div>
                              </div>
                            )}

                            {point.stat && (
                              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3 flex items-start gap-2">
                                <BarChart3 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-semibold text-amber-900 mb-1">Data Point</p>
                                  <p className="text-xs text-amber-800">{point.stat}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Way Forward - Reform Pathways */}
            {answer.way_forward && answer.way_forward.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">4</div>
                    <h3 className="text-lg font-bold text-green-900 uppercase">Reform & Way Forward</h3>
                  </div>
                  <div className="bg-white border-2 border-green-300 rounded-xl p-5">
                    <div className="grid gap-3 md:grid-cols-2">
                      {answer.way_forward.map((item, idx) => (
                        <div key={idx} className="flex gap-3 items-start">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx < 2 ? 'bg-emerald-100 text-emerald-900' :
                            idx < 4 ? 'bg-teal-100 text-teal-900' :
                            idx < 6 ? 'bg-cyan-100 text-cyan-900' :
                            'bg-blue-100 text-blue-900'
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="text-sm text-slate-800">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Forward-Looking Conclusion */}
            {answer.conclusion && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-emerald-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">5</div>
                    <h3 className="text-lg font-bold text-emerald-900 uppercase">Forward-Looking Conclusion</h3>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-800 leading-relaxed">{answer.conclusion}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Alternative Introductions */}
            {answer.alternates?.intros && answer.alternates.intros.length > 0 && (
              <Card className="border-indigo-200 bg-indigo-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">6</div>
                    <h3 className="text-lg font-bold text-indigo-900 uppercase">Alternative Introductions</h3>
                  </div>
                  <div className="space-y-3">
                    {answer.alternates.intros.map((intro, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-indigo-200">
                        <p className="text-xs font-semibold text-indigo-700 mb-2">Option {idx + 1}</p>
                        <p className="text-sm text-slate-800">{intro}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Alternative Conclusions */}
            {answer.alternates?.conclusions && answer.alternates.conclusions.length > 0 && (
              <Card className="border-violet-200 bg-violet-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">7</div>
                    <h3 className="text-lg font-bold text-violet-900 uppercase">Alternative Conclusions</h3>
                  </div>
                  <div className="space-y-3">
                    {answer.alternates.conclusions.map((conclusion, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-violet-200">
                        <p className="text-xs font-semibold text-violet-700 mb-2">Option {idx + 1}</p>
                        <p className="text-sm text-slate-800">{conclusion}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Topper Notes */}
            {answer.topper_notes?.bullets && answer.topper_notes.bullets.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">8</div>
                    <h3 className="text-lg font-bold text-amber-900 uppercase">Topper Notes & Policy Hooks</h3>
                  </div>
                  <div className="bg-white rounded-xl border-2 border-amber-200 p-5">
                    <ul className="space-y-2">
                      {answer.topper_notes.bullets.map((note, idx) => (
                        <li key={idx} className="flex gap-3 items-start">
                          <span className="flex-shrink-0 w-5 h-5 bg-amber-100 text-amber-900 rounded-full flex items-center justify-center text-xs font-bold">💡</span>
                          <span className="text-sm text-slate-800">{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Checklist */}
            {answer.checklist && answer.checklist.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">9</div>
                    <h3 className="text-lg font-bold text-green-900 uppercase">
                      GS-III Checklist
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {answer.checklist.map((item, idx) => (
                      <div key={idx} className="bg-white border border-green-200 rounded-lg p-3 flex items-center gap-3">
                        {item.present ? (
                          <CircleCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                        <span className="text-sm text-slate-800">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Score Estimate - Always out of 10 */}
            {answer.score_estimate !== undefined && (
              <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                        <Award className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-emerald-900">Score Estimate</h3>
                        <p className="text-xs text-emerald-600">Based on UPSC standards</p>
                      </div>
                    </div>
                    <div className="text-center bg-white rounded-xl px-4 py-2 border-2 border-emerald-200">
                      <p className="text-3xl font-bold text-emerald-900">{answer.score_estimate}/10</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Paywall Message */}
            {answer.paywall_message && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-lg p-6 text-center">
                <FileText className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-emerald-900 mb-2">Upgrade to PRO</h3>
                <p className="text-sm text-emerald-800">{answer.paywall_message}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // EVALUATION VIEW
  if (mode === "evaluation") {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-III Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <Sparkles className="w-8 h-8" />
                GS-III Smart Evaluation
              </h1>
              <p className="text-emerald-50">
                Policy logic, data discipline, interlinkages & outcome scoring
              </p>
            </div>
          </div>
        </div>

        {/* GS-III Evaluation Criteria */}
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Globe className="w-5 h-5 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-900">/10</span>
              </div>
              <p className="text-xs font-semibold text-emerald-900">Syllabus Alignment</p>
              <p className="text-[11px] text-emerald-700">Correct GS-III mapping</p>
            </CardContent>
          </Card>

          <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Factory className="w-5 h-5 text-teal-600" />
                <span className="text-xs font-bold text-teal-900">/10</span>
              </div>
              <p className="text-xs font-semibold text-teal-900">Policy Logic</p>
              <p className="text-[11px] text-teal-700">Scheme-Gap-Reform</p>
            </CardContent>
          </Card>

          <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Zap className="w-5 h-5 text-cyan-600" />
                <span className="text-xs font-bold text-cyan-900">/10</span>
              </div>
              <p className="text-xs font-semibold text-cyan-900">Feasibility</p>
              <p className="text-[11px] text-cyan-700">Implementation realism</p>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-bold text-blue-900">/10</span>
              </div>
              <p className="text-xs font-semibold text-blue-900">Data Discipline</p>
              <p className="text-[11px] text-blue-700">2-3 relevant data points</p>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-lime-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Leaf className="w-5 h-5 text-green-600" />
                <span className="text-xs font-bold text-green-900">/10</span>
              </div>
              <p className="text-xs font-semibold text-green-900">Interlinkages</p>
              <p className="text-[11px] text-green-700">Cross-sectoral links</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2 items-start">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Submit Your GS-III Answer</h2>
                  <p className="text-xs text-slate-600">Paste your GS-III question and answer for evaluation</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleEvaluate}>
                {/* Input Mode Toggle */}
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">INPUT METHOD</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={evalInputMode === "type" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEvalInputMode("type")}
                      className={evalInputMode === "type" ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Type Answer
                    </Button>
                    <Button
                      type="button"
                      variant={evalInputMode === "handwritten" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEvalInputMode("handwritten")}
                      className={evalInputMode === "handwritten" ? "bg-purple-500 hover:bg-purple-600" : ""}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Upload Handwritten
                    </Button>
                  </div>
                </div>

                {evalInputMode === "type" ? (
                  <>
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">QUESTION</p>
                      <Textarea
                        rows={3}
                        value={evalQuestion}
                        onChange={(e) => setEvalQuestion(e.target.value)}
                        placeholder="Paste the GS-III question you attempted..."
                        required
                        className="text-sm resize-none border-2 focus:border-emerald-400"
                      />
                    </div>

                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">YOUR ANSWER</p>
                      <Textarea
                        rows={10}
                        value={evalAnswer}
                        onChange={(e) => setEvalAnswer(e.target.value)}
                        placeholder="Paste your GS-III answer here..."
                        required
                        className="text-sm resize-none border-2 focus:border-emerald-400"
                      />
                    </div>

                    {!isPro && (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-xs text-amber-800">
                          🔒 Full evaluation with score breakdown, interlinkage analysis, and improved answer is PRO-only.
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-3"
                    >
                      {loading ? "Evaluating..." : "🎯 Evaluate My GS-III Answer"}
                    </Button>
                  </>
                ) : (
                  <HandwrittenUpload
                    marks={10}
                    suite="GS3"
                    onEvaluationComplete={(data) => {
                      setEvaluation(data);
                      if (data.extracted_question) setEvalQuestion(data.extracted_question);
                      if (data.extracted_answer) setEvalAnswer(data.extracted_answer);
                      toast({ description: "✅ Handwritten GS-III answer evaluated!" });
                    }}
                    onExtractedData={(data) => {
                      if (data.question) setEvalQuestion(data.question);
                      if (data.answer) setEvalAnswer(data.answer);
                    }}
                  />
                )}
              </form>
            </CardContent>
          </Card>

          {/* Evaluation Results - Matching GS1 UI */}
          {evaluation && (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-emerald-100 to-teal-100 p-2 rounded-lg">
                      <Award className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">GS-III Evaluation Results</h2>
                      <p className="text-xs text-slate-600">Examiner-aligned GS-III feedback</p>
                    </div>
                  </div>
                  {typeof evaluation.score_estimate === "number" && (
                    <div className="text-center">
                      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl">
                        <p className="text-xs font-semibold">Estimated Score</p>
                        <p className="text-2xl font-bold">{(evaluation.score_estimate / marks * 10).toFixed(1)}/10</p>
                      </div>
                    </div>
                  )}
                </div>

                {evaluation.intelligence && <IntelligencePanel intelligence={evaluation.intelligence} gaps={{ examples: evaluation.missing_examples, keywords: evaluation.keyword_suggestions }} />}

                {/* Detailed Scores - NEW */}
                {evaluation.scores && Object.keys(evaluation.scores).length > 0 && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">📊 Detailed Scores</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(evaluation.scores).map(([criterion, score]) => (
                          <div key={criterion} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <span className="text-xs font-medium text-slate-700 capitalize">
                              {criterion.replace(/_/g, " ")}
                            </span>
                            <Badge variant="outline" className="text-xs">{score}/10</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Strengths & Weaknesses - NEW */}
                {(evaluation.strongest_areas?.length > 0 || evaluation.weakest_areas?.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {evaluation.strongest_areas?.length > 0 && (
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <p className="text-sm font-bold text-green-900">Strongest Areas</p>
                          </div>
                          <ul className="space-y-1.5">
                            {evaluation.strongest_areas.map((area, idx) => (
                              <li key={idx} className="text-xs text-green-800 flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {evaluation.weakest_areas?.length > 0 && (
                      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <p className="text-sm font-bold text-amber-900">Weakest Areas</p>
                          </div>
                          <ul className="space-y-1.5">
                            {evaluation.weakest_areas.map((area, idx) => (
                              <li key={idx} className="text-xs text-amber-800 flex items-start gap-2">
                                <span className="text-amber-600">⚠</span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Structure & Flow */}
                {evaluation.structure_feedback?.length > 0 && (
                  <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-blue-600" />
                        <p className="text-sm font-bold text-blue-900">Structure & Policy Logic</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.structure_feedback.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <CheckCircle2 className="w-3 h-3 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span>{p.comment || p}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Depth & Coverage */}
                {evaluation.depth_feedback?.length > 0 && (
                  <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-emerald-600" />
                        <p className="text-sm font-bold text-emerald-900">Depth & Interlinkages</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.depth_feedback.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <TrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span>{p.comment || p}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Keywords to Add */}
                {false && evaluation.keyword_suggestions?.length > 0 && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-purple-600" />
                        <p className="text-sm font-bold text-purple-900">Keywords to Add</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {evaluation.keyword_suggestions.map((keyword, i) => (
                          <Badge key={i} className="bg-purple-100 text-purple-900 text-xs border-purple-300">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Missing Data & Examples */}
                {false && evaluation.missing_examples?.length > 0 && (
                  <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <p className="text-sm font-bold text-amber-900">Missing Data & Examples</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.missing_examples.map((example, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="text-amber-600 flex-shrink-0">•</span>
                            <span>{example}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Missing Elements (Detailed) - NEW */}
                {evaluation.missing_elements && (evaluation.missing_elements.keywords?.length > 0 || evaluation.missing_elements.data?.length > 0 || evaluation.missing_elements.examples?.length > 0) && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">🔍 Missing Elements</p>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        {evaluation.missing_elements.keywords?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Keywords</p>
                            <div className="flex flex-wrap gap-1.5">
                              {evaluation.missing_elements.keywords.map((kw, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {evaluation.missing_elements.data?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Data/Statistics</p>
                            <ul className="text-xs text-slate-600 space-y-1">
                              {evaluation.missing_elements.data.map((d, idx) => (
                                <li key={idx}>• {d}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {evaluation.missing_elements.examples?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Examples</p>
                            <ul className="text-xs text-slate-600 space-y-1">
                              {evaluation.missing_elements.examples.map((ex, idx) => (
                                <li key={idx}>• {ex}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* 10-Point Improvement Plan - NEW */}
                {evaluation.improvement_plan?.length > 0 && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">🎯 10-Point Improvement Plan</p>
                      </div>
                      <ol className="space-y-2">
                        {evaluation.improvement_plan.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-[10px] font-bold">
                              {idx + 1}
                            </span>
                            <span>{point.replace(/^\d+\.\s*/, '')}</span>
                          </li>
                        ))}
                      </ol>
                    </CardContent>
                  </Card>
                )}

                {/* Rewrite Suggestions - NEW */}
                {evaluation.rewrite_suggestions && (evaluation.rewrite_suggestions.introduction || evaluation.rewrite_suggestions.key_paragraph) && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Pencil className="w-4 h-4 text-purple-600" />
                        <p className="text-sm font-bold text-purple-900">✏️ Rewrite Suggestions</p>
                      </div>
                      {evaluation.rewrite_suggestions.introduction && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-purple-700">Improved Introduction</p>
                          <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                            {evaluation.rewrite_suggestions.introduction}
                          </p>
                        </div>
                      )}
                      {evaluation.rewrite_suggestions.key_paragraph && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-purple-700">Rewritten Key Paragraph</p>
                          <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                            {evaluation.rewrite_suggestions.key_paragraph}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Better Introduction */}
                {evaluation.better_intro && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">Suggested Better Introduction</p>
                      </div>
                      <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                        {evaluation.better_intro}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Better Conclusion */}
                {evaluation.better_conclusion && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">Suggested Better Conclusion</p>
                      </div>
                      <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                        {evaluation.better_conclusion}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Improved Model Answer */}
                {evaluation.improved_answer && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">Improved Model Answer</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 max-h-96 overflow-y-auto text-xs text-slate-700 space-y-2">
                        {evaluation.improved_answer.split("\n").map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Meta Tips */}
                {evaluation.meta_tips?.length > 0 && (
                  <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-rose-600" />
                        <p className="text-sm font-bold text-rose-900">Meta Tips for Improvement</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.meta_tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="text-rose-600 flex-shrink-0 font-bold">→</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // EXPECTED QUESTIONS VIEW
  if (mode === "expected-questions") {
    // PRO-only feature check
    if (!isPro) {
      return (
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-III Suite</Button>
          <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Trend-Based Questions is PRO-Only</h2>
              <p className="text-slate-600 max-w-md mx-auto">
                Get Smart predicted questions with probability analysis, PYQ hooks, and current affairs links. Upgrade to PRO to unlock this powerful feature.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8"
                  onClick={() => window.location.href = '/upgrade'}
                >
                  Upgrade to PRO
                </Button>
                <Button variant="outline" onClick={() => setMode("home")}>
                  Back to Suite
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-III Suite</Button>
          <Badge className="px-4 py-2 text-sm bg-gradient-to-r from-amber-500 to-orange-500 text-white">⭐ PRO</Badge>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900">🎯 GS-III Trend-Based Questions</h1>
            <p className="text-xs text-slate-600">
              Smart predicted GS-III questions based on past 1 year Current Affairs & PYQ patterns
            </p>
          </div>
        </div>

        {expectedData && (
          <Card className="border-slate-200 bg-white/90">
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="flex-1 text-[11px] text-slate-700">Based on latest current affairs and GS-III syllabus</p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-slate-600 text-[11px]">Subject:</label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-800"
                >
                  {GS3_SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={fetchExpectedQuestions}
                  disabled={loading}
                  className="text-[11px] rounded-full px-4 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && (
          <Card className="border-slate-200 bg-white/90">
            <CardContent className="p-8 text-center">
              <div className="inline-block w-10 h-10 border-4 border-t-emerald-600 border-r-transparent border-b-emerald-600 border-l-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-slate-600">Loading GS-III questions...</p>
            </CardContent>
          </Card>
        )}

        {!loading && expectedData && expectedData.questions && expectedData.questions.length > 0 && (
          <div className="space-y-3">
            {expectedData.questions.map((q, idx) => {
              const qText = typeof q === 'string' ? q : (q.question || "");
              const qId = q.id || `q-${idx}`;
              const isPracticed = practicedIds.includes(qId);

              return (
                <Card key={idx} className="border-slate-200 bg-white/90 hover:shadow-md transition-shadow">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-[11px] font-semibold text-emerald-800">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        {q.probability_tag && (
                          <Badge variant="outline" className={`text-[10px] mb-2 ${q.probability_tag === '🔒 PRO' ? 'bg-slate-100 text-slate-500 border-slate-300' : 'bg-emerald-50 text-emerald-800 border-emerald-300'}`}>
                            {q.probability_tag}
                          </Badge>
                        )}
                        {q.theme && (
                          <p className="text-[10px] text-slate-500 mb-1 font-medium">Theme: {q.theme}</p>
                        )}
                        <p className="text-sm text-slate-800 font-medium mb-2">{qText}</p>
                        
                        {q.why_likely && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-2">
                            <p className="text-[10px] font-semibold text-blue-900 mb-1">💡 Why this question is likely:</p>
                            <p className="text-[11px] text-blue-800">{q.why_likely}</p>
                          </div>
                        )}
                        
                        {q.pyq_hook && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 mb-2">
                            <p className="text-[10px] font-semibold text-purple-900 mb-1">📚 PYQ Pattern:</p>
                            <p className="text-[11px] text-purple-800">{q.pyq_hook}</p>
                          </div>
                        )}
                        
                        {q.current_link && (
                          <div className="text-[11px] text-green-700 mb-2">
                            <span className="font-semibold">🔗 Current Link:</span> {q.current_link}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendToMains(qText, qId)}
                          className="text-[10px] px-2 py-1 h-auto"
                        >
                          Send to GS-III
                        </Button>
                        {!isPracticed && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMarkPracticed(qId)}
                            className="text-[10px] px-2 py-1 h-auto text-green-700 hover:text-green-800 hover:bg-green-50"
                          >
                            Mark Practiced
                          </Button>
                        )}
                        {isPracticed && (
                          <span className="text-[10px] text-green-700 px-2 py-1">✓ Practiced</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // PYQ PATTERN VIEW - Exactly like GS-II with emerald theme
  if (mode === "pyq-pattern") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-III Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <TrendingUp className="w-8 h-8" />
                GS-III PYQ Pattern Analysis
              </h1>
              <p className="text-emerald-50">
                Discover 20+ years of GS-III question trends, frequency analysis & topic forecasting
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">SELECT GS-III SUBJECT</label>
                <select
                  value={pyqSubject}
                  onChange={(e) => setPyqSubject(e.target.value)}
                  className="w-full rounded-md border-2 border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 focus:border-emerald-400 focus:outline-none"
                >
                  {GS3_SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">YEAR RANGE</label>
                <div className="flex gap-2">
                  {["5", "10", "15", "20"].map((years) => (
                    <Button
                      key={years}
                      type="button"
                      variant={pyqYearRange === years ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPyqYearRange(years)}
                      className={pyqYearRange === years ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                    >
                      {years} Years
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={fetchPyqPattern}
              disabled={loading}
              className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold"
            >
              {loading ? "Analyzing PYQ Patterns..." : "🔍 Analyze GS-III PYQ Patterns"}
            </Button>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-8 text-center">
              <div className="inline-block w-10 h-10 border-4 border-t-emerald-600 border-r-transparent border-b-emerald-600 border-l-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-slate-600">Analyzing {pyqYearRange} years of GS-III PYQ data...</p>
            </CardContent>
          </Card>
        )}

        {/* PYQ Pattern Display */}
        {!loading && pyqData && (
          <div className="space-y-6">
            {/* Summary Stats */}
            {pyqData.summary && (
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-900">{pyqData.summary.total_questions || 0}</p>
                    <p className="text-xs text-emerald-700 mt-1">Total Questions</p>
                  </CardContent>
                </Card>
                <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-teal-900">{pyqData.summary.avg_per_year || 0}</p>
                    <p className="text-xs text-teal-700 mt-1">Avg Questions/Year</p>
                  </CardContent>
                </Card>
                <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-cyan-900">{pyqData.summary.most_frequent_topic || "N/A"}</p>
                    <p className="text-xs text-cyan-700 mt-1">Most Frequent Topic</p>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-gradient-to-br from-green-50 to-lime-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-900">{pyqData.summary.trend || "↗️"}</p>
                    <p className="text-xs text-green-700 mt-1">Trend Direction</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Topic Frequency Analysis */}
            {pyqData.topic_frequency && pyqData.topic_frequency.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                    Topic Frequency Analysis
                  </h3>
                  <div className="space-y-3">
                    {pyqData.topic_frequency.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-900 rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-slate-900">{item.topic}</span>
                            <span className="text-sm font-bold text-emerald-600">{item.count} Qs</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
                              style={{ width: `${(item.count / (pyqData.summary?.total_questions || 100)) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Year-wise Trends */}
            {pyqData.year_wise && pyqData.year_wise.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    Year-wise Question Trends
                  </h3>
                  <div className="space-y-2">
                    {pyqData.year_wise.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm font-semibold text-slate-900">{item.year}</span>
                        <Badge variant="secondary">{item.count} Questions</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actual PYQs */}
            {pyqData.questions && pyqData.questions.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Previous Year Questions ({pyqData.questions.length})
                  </h3>
                  <div className="space-y-3">
                    {pyqData.questions.map((q, idx) => (
                      <Card key={idx} className="border-emerald-200 bg-emerald-50/50 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <Badge variant="outline" className="bg-emerald-100 text-emerald-900 border-emerald-300">
                                {q.year}
                              </Badge>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-slate-800 font-medium mb-2">{q.question}</p>
                              {q.marks && (
                                <Badge variant="secondary" className="text-xs">{q.marks} marks</Badge>
                              )}
                              {q.topic && (
                                <Badge variant="secondary" className="text-xs ml-2 bg-teal-100 text-teal-900">
                                  {q.topic}
                                </Badge>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setQuestion(q.question);
                                if (q.marks) setMarks(q.marks);
                                setMode("answer-writing");
                              }}
                              className="text-xs"
                            >
                              Practice
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Insights */}
            {pyqData.insights && pyqData.insights.length > 0 && (
              <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-teal-900 mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-teal-600" />
                    Key Insights & Forecasting
                  </h3>
                  <ul className="space-y-2">
                    {pyqData.insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-800">
                        <span className="text-teal-600 flex-shrink-0 mt-0.5">💡</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && !pyqData && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-12 text-center">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-base font-bold text-slate-900 mb-2">No PYQ Analysis Yet</p>
              <p className="text-sm text-slate-600">
                Select a GS-III subject and year range, then click &ldquo;Analyze GS-III PYQ Patterns&rdquo;
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return null;
};

export default GS3SuitePage;
